import React from 'react';
import { cn } from '@frontend/shadcn/lib/utils';

// --- HighlightTabRoot ---
interface HighlightTabRootProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
}

const HighlightTabRoot = React.forwardRef<
  HTMLButtonElement,
  HighlightTabRootProps
>(({ className, isActive, children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={!!isActive}
      className={cn(
        'w-full',
        'flex flex-col items-start justify-center', // Manages internal layout of children
        'rounded-xl',
        'p-2',
        'gap-[2px]', // Default gap between heading and info, if they are direct children
        'border-[1px]',
        'border-t-[3px]',
        isActive ? 'border-t-slate-600' : 'border-t-slate-200',
        'bg-card hover:bg-muted/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'transition-colors duration-150 ease-in-out',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
HighlightTabRoot.displayName = 'HighlightTabRoot';

// --- HighlightTabHeading ---
interface HighlightTabHeadingProps extends React.HTMLAttributes<HTMLSpanElement> {}

const HighlightTabHeading = React.forwardRef<
  HTMLSpanElement,
  HighlightTabHeadingProps
>(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        'font-bold leading-tight',
        'text-sm text-card-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
});
HighlightTabHeading.displayName = 'HighlightTabHeading';

// --- HighlightTabInfo ---
interface HighlightTabInfoProps extends React.HTMLAttributes<HTMLSpanElement> {}

const HighlightTabInfo = React.forwardRef<
  HTMLSpanElement,
  HighlightTabInfoProps
>(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        'leading-tight',
        'text-xs text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
});
HighlightTabInfo.displayName = 'HighlightTabInfo';

// Export the composable components
export {
  HighlightTabRoot,
  HighlightTabHeading,
  HighlightTabInfo,
  type HighlightTabRootProps,
  type HighlightTabHeadingProps,
  type HighlightTabInfoProps,
};
